/* Parses brain/vidmax/plan/bets.md (the goal + bets source of truth) for the home Mission and Bets widgets.
   Shape: "## Goal" bullets "- **Label:** text", "### Timeline" bullets, then "## <Section>" headings whose
   "### <ID> · <Title>" entries carry "- **Field:** value" bullets. Mirrors claude/skills/wf-vidmax-plan/home-search.ts. */

export interface IBetGoalItem {
  label: string
  text: string
}

export interface IBet {
  id: string
  title: string
  /** The "## ..." heading the bet sits under (Active, Candidates, Experiments, ...) */
  section: string
  rank: number | null
  fields: Record<string, string>
}

export interface IBetsSummary {
  goal: IBetGoalItem[]
  timeline: IBetGoalItem[]
  bets: IBet[]
}

const BULLET = /^- \*\*([^*]+?):?\*\*:? (.+)$/
const stripMarkdown = (text: string): string =>
  text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .trim()

export const parseBets = (markdown: string): IBetsSummary => {
  const out: IBetsSummary = { goal: [], timeline: [], bets: [] }
  let section = ""
  let sub = ""
  let bet: IBet | null = null
  for (const raw of markdown.replace(/<!--[\s\S]*?-->/g, "").split("\n")) {
    const line = raw.trimEnd()
    const h2 = line.match(/^## (.+)$/)
    if (h2) {
      section = (h2[1] ?? "").replace(/\s*\(.*\)$/, "").trim()
      sub = ""
      bet = null
      continue
    }
    const h3 = line.match(/^### (.+)$/)
    if (h3) {
      const heading = h3[1] ?? ""
      const parts = heading.split(" · ")
      if (parts.length >= 2 && !/^goal/i.test(section)) {
        bet = { id: parts[0]?.trim() ?? "", title: parts.slice(1).join(" · ").trim(), section, rank: null, fields: {} }
        out.bets.push(bet)
      } else {
        bet = null
      }
      sub = heading.trim().toLowerCase()
      continue
    }
    const bullet = line.match(BULLET)
    if (!bullet) continue
    const label = (bullet[1] ?? "").trim()
    const text = stripMarkdown(bullet[2] ?? "")
    if (bet) {
      bet.fields[label] = text
      if (label === "Rank") bet.rank = Number.parseInt(text, 10) || null
    } else if (/^goal/i.test(section)) {
      ;(sub === "timeline" ? out.timeline : out.goal).push({ label, text })
    }
  }
  return out
}

export const activeBets = (summary: IBetsSummary): IBet[] => summary.bets.filter((b) => /^active/i.test(b.section))

/** Everything that isn't active or done, by rank (unranked last). */
export const rankedBets = (summary: IBetsSummary): IBet[] =>
  summary.bets
    .filter((b) => !/^(active|done|shipped|killed|archive)/i.test(b.section))
    .sort((a, b) => (a.rank ?? 1e9) - (b.rank ?? 1e9))

export interface IMissionSource {
  /** Company slug = the brain/ folder name (vidmax, pebble, …) */
  company: string
  path: string
  summary: IBetsSummary
}

const BETS_PATH = /brain\/([^/]+)\/plan\/bets\.md$/

/** Glob result (path → bets.md text) → one parsed source per company, sorted by slug. */
export const missionSources = (files: Record<string, string>): IMissionSource[] =>
  Object.entries(files)
    .flatMap(([path, markdown]) => {
      const company = path.match(BETS_PATH)?.[1]
      return company ? [{ company, path: `brain/${company}/plan/bets.md`, summary: parseBets(markdown) }] : []
    })
    .sort((a, b) => a.company.localeCompare(b.company))
